export const slug = "sharing-tree";
export const title = "Sharing Tree";
export const section = "Programs";
export const content = `
# Sharing Tree

Sharing Tree is your food bank's seasonal gift program. Eligible households receive vouchers, gift cards, or toys based on their household size and tier.

## Program Overview

- Households must be **enrolled** in the program by a staff member before they can receive benefits.
- Each household is assigned a **tier** based on the number of members.
- Benefits vary by tier and are configured under **Settings → Sharing Tree**.

## Enrolling a Household

1. Open the client's detail page.
2. Click **Enroll in Sharing Tree**.
3. Confirm the household size — the system will auto-assign the correct tier.
4. Click **Save Enrollment**.

## Managing Gift Cards, Vouchers & Toys

On the Sharing Tree page, each enrolled household row shows the benefits assigned to them. Use the action buttons to:

- **Mark Received** — confirm a household picked up their items.
- **Edit Benefits** — adjust what a household receives (requires Admin or Writer role).
- **Remove Enrollment** — unenroll a household from the current year's program.

## Pickup Windows

Pickup windows define the time slots when households can collect their benefits. Windows are configured under **Settings → Sharing Tree → Pickup Windows**.

Staff can view and filter households by their assigned pickup window from the Sharing Tree page.

## Sending Reminders

Use the **Send Reminder** button to email or text enrolled households with their pickup window details. You can send reminders to:

- All enrolled households
- Households who have not yet picked up

A reminder badge on each row shows when the last reminder was sent.

## Bulk Reminder Emails

Select multiple households using the checkboxes and choose **Send Reminder to Selected** from the bulk actions menu.

## Duplicate Detection

Sharing Tree runs its own duplicate check to prevent the same household from being enrolled twice under different client records. Suspected duplicates appear with a warning badge — click to compare and merge if needed.

## Print-Ready Voucher List

Click **Export Voucher List** to download a print-ready PDF or CSV sorted by tier. This is designed for staff prepping packets before the distribution event.

## Toy Item Tags and Private Notes

For children receiving toys, type to search the item suggestions curated from the 2025 requests. Select a suggestion, or click **Add** / press **Enter** to add a custom item. Use short item names; spaces and commas are allowed within an item. Remove a choice with its remove button. Each request can have up to 12 items, with at most 60 characters per item.

Clients can enter additional information in **Notes for staff**. Staff can read and edit those notes, but they do not appear on public tree labels or become shared suggestions. Custom item choices belong to that member's request and are not automatically added to the shared catalog.

Catalog items are preapproved. Custom items cannot appear on public labels until staff open the household edit dialog, review the wording for names, contact details, and private medical information, and check the custom-item approval box before saving. Saving notes alone does not approve custom items. Changing the selected items clears that approval.

Old long-form requests remain available for staff review in the household edit dialog. Choose concise item tags before printing; do not copy personal details into public items.

## Printing Information Labels for Tree Cards

Use **Print labels** beside the toy, clothing, or gift-card report. These are information stickers for existing cards, not complete cards or artwork. **Export CSV** remains a separate download.

Choose the program year first, then enter the Avery product number. The supported US Letter layouts are:

| Avery product | Label size | Labels per sheet |
| --- | --- | --- |
| [5160 / 8160](https://www.avery.com/templates/5160) | 1 × 2-5/8 inches | 30 (3 columns × 10 rows) |
| [5163 / 8163](https://www.avery.com/templates/5163) | 2 × 4 inches | 10 (2 columns × 5 rows) |
| [22816](https://www.avery.com/templates/22816) | 2 × 2 inches | 12 (3 columns × 4 rows) |

Dimensions and placement follow Avery's official blank templates. Other product numbers are not supported; an unknown number blocks printing instead of assuming a layout.

Select members by their IDs. Boy/Girl wording, abbreviated clothing sizes, and short household/member IDs such as **H42 / M177** are on by default and can be turned off independently. Clothing labels let you choose Pant, Shirt, and PJ size lines. Gift-card labels show no amount.

For a partly used sheet, set **Start position** to the first unused label, counting left to right and then down. This skips positions only on the first sheet. Check the label and sheet totals and the preview.

Labels contain gender, age, the selected public items or clothing sizes, and IDs—not names, contact information, private notes, or unreviewed original narratives. Printing is blocked if selected content is missing or does not fit. Choose larger stock, fewer items or clothing lines, or remove affected members from the selection.

Print on **US Letter (8.5 × 11 inches)** at **actual size / 100%**, with browser headers and footers disabled. First test on plain paper and compare it with the label sheet; printer feed alignment still needs a physical check.

## Printing Sharing Tree Postcards

From the Sharing Tree page, click **Print postcard mailer** to prepare a postcard for the selected program year. Before printing, confirm that the selected year's signup opening date, deadline, and **mailer help phone** are configured under **Settings → Sharing Tree**. The postcard uses that year's settings only; it does not fill missing dates or a phone number from an earlier year. Paper-return instructions are not required.

The destination must be the published public HTTPS client portal login page ending in **/portal/login**. It must not be a staff page, Saturday check-in page, development or preview address, or a URL containing a query string, token, or fragment; the preview blocks development/preview destinations. Verify the destination in the preview and check the confirmation box; printing remains blocked until required information fits and the destination has been confirmed. Households need email access to receive the one-time sign-in link or set up their account.

Print the **6 × 4 inch landscape** postcard at **actual size / 100%** on 4 × 6 inch paper, with browser headers and footers disabled. Only the information side is printed; leave the reverse blank for manual addressing. The optional annual message is omitted from the card to preserve legibility. Pickup details are shared after signup, so the postcard does not promise a specific pickup window.
`;
